import { useState } from "react";
import { ChevronDown } from "lucide-react";
import logoWhite from "@/assets/cherry-hill-logo-white.webp";
import { useSiteSettings } from "@/hooks/useSiteSettings";

type LinkItem = { label: string; href: string; noModal?: boolean; comingSoon?: boolean };

const programs: LinkItem[] = [
  { label: "Partial Hospitalization (PHP)", href: "/php-cherry-hill-nj" },
  { label: "Intensive Outpatient (IOP)", href: "/iop-cherry-hill-nj" },
  { label: "Evening IOP", href: "/evening-iop-new-jersey" },
  { label: "Virtual IOP", href: "/virtual-iop-new-jersey" },
  { label: "Detox Referral", href: "/detox-referral-coordination-nj" },
  { label: "MAT & Suboxone", href: "/medication-assisted-treatment-nj" },
  { label: "Psychiatric Evaluations", href: "/psychiatric-evaluation-nj" },
  { label: "Treatment for Professionals", href: "/addiction-treatment-professionals-nj" },
  { label: "Veterans & First Responders", href: "/veterans-first-responders-addiction-treatment-nj" },
];


const conditions: LinkItem[] = [
  { label: "Heroin & Opioids", href: "/heroin-opioid-addiction-treatment-nj" },
  { label: "Alcohol Addiction", href: "/alcohol-addiction-treatment-cherry-hill-nj" },
  { label: "Drug Rehab (All Substances)", href: "/drug-rehab-cherry-hill-nj" },
  { label: "Fentanyl Addiction", href: "/fentanyl-addiction-treatment-nj" },
  { label: "Cocaine Addiction", href: "/cocaine-addiction-treatment-nj" },
  { label: "Benzodiazepines", href: "/benzodiazepine-addiction-treatment-nj" },
  { label: "Prescription Painkillers", href: "/painkiller-addiction-treatment-nj" },
  { label: "Methadone", href: "/methadone-addiction-treatment-nj" },
  { label: "Synthetic Drugs (K2, Zaza)", href: "/synthetic-drug-treatment-nj" },
];

const quickLinks: LinkItem[] = [
  { label: "About Us", href: "/about" },
  { label: "Contact", href: "/contact" },
  { label: "Blog", href: "/addiction-resources-new-jersey" },
  { label: "Verify Insurance", href: "/verify-insurance", noModal: true },
  { label: "Horizon BCBSNJ", href: "/insurance/horizon-blue-cross-blue-shield-nj-rehab" },
  { label: "UMR", href: "/insurance/umr-rehab-nj" },
  { label: "Privacy Policy", href: "/privacy-policy" },
];


const camdenLinks: LinkItem[] = [
  { label: "Cherry Hill", href: "/" },
  { label: "Voorhees", href: "/addiction-treatment-voorhees-nj" },
  { label: "Marlton", href: "/addiction-treatment-marlton-nj" },
  { label: "Haddonfield", href: "/addiction-treatment-haddonfield-nj" },
  { label: "Moorestown", href: "/addiction-treatment-moorestown-nj" },
  { label: "Camden", href: "/addiction-treatment-camden-nj" },
];

const burlingtonLinks: LinkItem[] = [
  { label: "Mount Laurel", href: "/addiction-treatment-mount-laurel-nj" },
  { label: "Medford", href: "/addiction-treatment-medford-nj" },
  { label: "Cinnaminson", href: "/addiction-treatment-cinnaminson-nj" },
];

const gloucesterLinks: LinkItem[] = [
  { label: "Turnersville", href: "/addiction-treatment-turnersville-nj" },
  { label: "Sewell", href: "/addiction-treatment-sewell-nj" },
  { label: "Glassboro", href: "/addiction-treatment-glassboro-nj" },
];

const headingStyle: React.CSSProperties = {
  fontFamily: "Helvetica, Arial, sans-serif",
  fontWeight: 700,
  fontSize: "11px",
  color: "rgba(250,244,240,0.4)",
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  marginBottom: "12px",
  display: "block",
};

const subHeadingStyle: React.CSSProperties = {
  fontFamily: "Helvetica, Arial, sans-serif",
  fontSize: "10px",
  color: "rgba(250,244,240,0.35)",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  marginBottom: "6px",
  display: "block",
};

const linkStyle: React.CSSProperties = {
  display: "block",
  fontFamily: "Helvetica, Arial, sans-serif",
  fontSize: "13px",
  color: "rgba(250,244,240,0.62)",
  lineHeight: 2.0,
  textDecoration: "none",
  transition: "color 0.15s",
};

const LinkList = ({ items }: { items: LinkItem[] }) => (
  <>
    {items.map((l) =>
      l.comingSoon ? (
        <span
          key={l.label + l.href}
          style={{
            ...linkStyle,
            color: "rgba(250,244,240,0.32)",
            cursor: "default",
            fontStyle: "italic",
          }}
          title="Coming soon"
        >
          {l.label}
        </span>
      ) : (
        <a
          key={l.label + l.href}
          href={l.href}
          className="ftr-link"
          style={linkStyle}
          {...(l.noModal ? { "data-no-modal": "true" } : {})}
        >
          {l.label}
        </a>
      )
    )}
  </>
);

const Footer = () => {
  const [areasOpen, setAreasOpen] = useState(false);
  const settings = useSiteSettings();

  return (
    <footer className="bg-navy-deep text-primary-foreground">
      <style>{`
        .ftr-link:hover { color: #FAF4F0 !important; }
        .ftr-divider { height: 1px; background: rgba(255,255,255,0.08); margin: 16px 0; }
        .ftr-grid { display: grid; grid-template-columns: 1.2fr 1fr 1fr 1fr; gap: 40px; max-width: 1200px; margin: 0 auto; padding: 48px 40px; }
        .ftr-areas-toggle { display: none; }
        .ftr-areas-content { display: block; }
        .ftr-bar { padding: 14px 40px; max-width: 1200px; margin: 0 auto; }
        @media (max-width: 1023px) {
          .ftr-grid { grid-template-columns: 1fr 1fr; }
        }
        @media (max-width: 767px) {
          .ftr-grid { grid-template-columns: 1fr; gap: 28px; padding: 32px 20px; }
          .ftr-cols-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 28px; }
          .ftr-areas-toggle { display: flex; align-items: center; justify-content: space-between; width: 100%; background: transparent; border: none; padding: 0; cursor: pointer; color: rgba(250,244,240,0.4); font-family: Helvetica, Arial, sans-serif; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 12px; }
          .ftr-areas-content { display: none; }
          .ftr-areas-content.open { display: block; }
          .ftr-bar { padding: 14px 20px; flex-direction: column !important; text-align: center; gap: 6px !important; }
          .ftr-bar > * { justify-content: center !important; }
          .ftr-bar-sep { display: none !important; }
        }
        @media (min-width: 768px) {
          .ftr-cols-2 { display: contents; }
        }
      `}</style>

      {/* Row 1 — Main footer */}
      <div className="ftr-grid">
        {/* Column 1 — Brand */}
        <div>
          <a href="/" style={{ display: "inline-block", marginBottom: "14px" }}>
            <img width={160} height={40}
              src={logoWhite}
              alt="Cherry Hill Recovery Center"
              loading="lazy"
              decoding="async"
              style={{ width: "160px", height: "auto" }}
            />
          </a>
          <p
            style={{
              fontSize: "13px",
              color: "rgba(250,244,240,0.65)",
              lineHeight: 1.7,
              marginBottom: "16px",
            }}
          >
            Board-certified addiction treatment in Cherry Hill, NJ — PHP, IOP, MAT, and dual-diagnosis care for South Jersey and the greater Philadelphia region.
          </p>
          <a
            href={`tel:${settings.phone_tel}`}
            className="ftr-link"
            style={{
              display: "block",
              fontFamily: "Helvetica, Arial, sans-serif",
              fontWeight: 700,
              fontSize: "15px",
              color: "#FAC8C8",
              textDecoration: "none",
              marginBottom: "4px",
            }}
          >
            {settings.phone_display}
          </a>
          <a
            href={`mailto:${settings.email}`}
            className="ftr-link"
            style={{
              display: "block",
              fontSize: "12px",
              color: "rgba(250,244,240,0.55)",
              textDecoration: "none",
            }}
          >
            {settings.email}
          </a>
          <address
            style={{
              fontSize: "12px",
              color: "rgba(250,244,240,0.45)",
              lineHeight: 1.6,
              marginTop: "8px",
              fontStyle: "normal",
              whiteSpace: "pre-line",
            }}
          >
            {settings.address}
          </address>
          <div
            style={{
              fontSize: "11px",
              color: "rgba(250,244,240,0.4)",
              marginTop: "8px",
            }}
          >
            Admissions available 24/7
          </div>
        </div>

        {/* Wrapper for mobile 2-col */}
        <div className="ftr-cols-2" style={{ display: "contents" }}>
          {/* Column 2 — Treatment Programs */}
          <div>
            <span style={headingStyle}>Treatment Programs</span>
            <LinkList items={programs} />
          </div>

          {/* Column 3 — Conditions + Quick Links */}
          <div>
            <span style={headingStyle}>Conditions We Treat</span>
            <LinkList items={conditions} />
            <div className="ftr-divider" />
            <span style={headingStyle}>Quick Links</span>
            <LinkList items={quickLinks} />
          </div>
        </div>

        {/* Column 4 — Areas We Serve */}
        <div>
          <button
            type="button"
            className="ftr-areas-toggle"
            onClick={() => setAreasOpen((v) => !v)}
            aria-expanded={areasOpen}
          >
            <span>Areas We Serve</span>
            <ChevronDown
              size={14}
              style={{
                transform: areasOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
              }}
            />
          </button>
          <span style={headingStyle} className="ftr-areas-desktop-heading">
            Areas We Serve
          </span>
          <style>{`
            @media (max-width: 767px) { .ftr-areas-desktop-heading { display: none !important; } }
          `}</style>

          <div className={`ftr-areas-content ${areasOpen ? "open" : ""}`}>
            <span style={subHeadingStyle}>Camden County</span>
            <LinkList items={camdenLinks} />

            <span style={{ ...subHeadingStyle, marginTop: "12px" }}>Burlington County</span>
            <LinkList items={burlingtonLinks} />

            <span style={{ ...subHeadingStyle, marginTop: "12px" }}>Gloucester County</span>
            <LinkList items={gloucesterLinks} />

            <a
              href="/service-areas"
              className="ftr-link"
              style={{
                display: "block",
                marginTop: "12px",
                fontFamily: "Helvetica, Arial, sans-serif",
                fontWeight: 700,
                fontSize: "12px",
                color: "#FAC8C8",
                textDecoration: "none",
              }}
            >
              View All Locations →
            </a>
          </div>
        </div>
      </div>

      {/* Row 2 — Accreditation bar */}
      <div
        style={{
          background: "rgba(0,0,0,0.15)",
          borderTop: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <div
          className="ftr-bar"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "12px",
            fontFamily: "Helvetica, Arial, sans-serif",
            fontSize: "11px",
            color: "rgba(250,244,240,0.4)",
            letterSpacing: "0.05em",
          }}
        >
          <span>NJ DMHAS License #2000881</span>
          <span className="ftr-bar-sep">·</span>
          <span>Joint Commission Accredited</span>
          <span className="ftr-bar-sep">·</span>
          <span>LegitScript Certified</span>
        </div>
      </div>

      {/* Row 3 — Copyright */}
      <div
        style={{
          background: "rgba(0,0,0,0.15)",
          borderTop: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <div
          className="ftr-bar"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "12px",
            fontFamily: "Helvetica, Arial, sans-serif",
            fontSize: "11px",
            color: "rgba(250,244,240,0.3)",
          }}
        >
          <span>© 2026 Cherry Hill Recovery Center. All Rights Reserved.</span>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <a
              href="/privacy-policy"
              className="ftr-link"
              style={{ color: "rgba(250,244,240,0.3)", textDecoration: "none", fontSize: "11px" }}
            >
              Privacy Policy
            </a>
            <span className="ftr-bar-sep">·</span>
            <span>
              Built by{" "}
              <a
                href="https://mikogrowthlab.com"
                target="_blank"
                rel="noopener noreferrer"
                className="ftr-link"
                style={{ color: "rgba(250,244,240,0.5)", textDecoration: "none", fontSize: "11px" }}
              >
                Miko Growth Lab
              </a>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
